import React, { JSX, useCallback, useEffect, useState } from "react";
import { useIsSmallScreen, usePopover } from "../../App";
import { Tone } from "../../models";
import {
    DARKENED_BACKGROUND_COLOR,
    HORIZONTAL_RULE_COLOR,
    TEXT_COLOR,
    WIDTH,
} from "../../tokens";
import { useSettings } from "../../utils/settings";
import {
    getTransposeByFromMode,
    getTransposeByFromToneObject,
} from "../eb-chant/note-utils";
import { LIBER_TONES, getTone } from "../tones";
import { PlayButton } from "./PlayButton";
import "./Player.css";
import { ToneDiagram } from "./ToneDiagram";

const getScoreFromTone = (
    tone: Tone,
    intonationType: "include" | "offset" | "skip",
    mediantType: "include" | "offset" | "skip",
    endType: "include" | "offset" | "skip"
): { score: string[]; offset: number } => {
    const score: string[] = [];
    let offset = 0;

    const processSyllable = (syllable: string, include: boolean) => {
        let notesInSyllable = 0;
        Array.from(syllable).forEach((char) => {
            if (char === "/" || char === "~") {
                // nothing
            } else if (char === "?") {
                if (include) {
                    score.pop();
                } else {
                    offset -= 1;
                }
            } else if (char === ".") {
                if (include) {
                    if (notesInSyllable === 2) {
                        score[score.length - 1] += ".";
                        score[score.length - 2] += ".";
                    } else {
                        score[score.length - 1] += ".";
                    }
                }
            } else if (char === "b") {
                if (include) {
                    score[score.length - 1] += "b";
                }
            } else {
                notesInSyllable++;
                if (include) {
                    score.push(char);
                } else {
                    offset++;
                }
            }
        });
    };

    if (intonationType !== "skip") {
        tone.intonation.forEach((syllable) => {
            processSyllable(syllable, intonationType === "include");
        });
    }

    if (mediantType !== "skip") {
        if (mediantType === "include") {
            score.push(`${tone.recitingNote}.`);
        }

        tone.mediant.forEach((syllable) => {
            processSyllable(syllable, mediantType === "include");
        });
    }

    if (mediantType === "include" && endType === "include") {
        score.push(",");
    } else if (mediantType === "offset" && endType === "include") {
        offset++;
    }

    if (endType !== "skip") {
        if (endType === "include") {
            score.push(`${tone.secondRecitingNote ?? tone.recitingNote}.`);
        }

        tone.ending.forEach((syllable) => {
            processSyllable(syllable, endType === "include");
        });
    }

    return { score, offset };
};

const getStartFromTone = (tone: Tone) => {
    const score: string[] = [];

    const processSyllable = (syllable: string) => {
        let notesInSyllable = 0;
        Array.from(syllable).forEach((char) => {
            if (char === "/" || char === "~") {
                // nothing
            } else if (char === "?") {
                score.pop();
            } else if (char === ".") {
                if (notesInSyllable === 2) {
                    score[score.length - 1] += ".";
                    score[score.length - 2] += ".";
                } else {
                    score[score.length - 1] += ".";
                }
            } else if (char === "b") {
                score[score.length - 1] += "b";
            } else {
                notesInSyllable++;
                score.push(char);
            }
        });
    };

    tone.intonation.forEach((syllable) => {
        processSyllable(syllable);
    });

    score[score.length - 1] += ".";

    if (score[0] !== score[score.length - 1]) {
        score.push(score[0] + ".");
    }

    return score;
};

export const getToneTitle = (tone: string, isSmallScreen: boolean) => {
    if (isSmallScreen) {
        return tone.includes("Tonus")
            ? tone.replace("Tonus Peregrinus", "T.P.")
            : `Tone ${tone}`;
    } else {
        return tone.includes("Tonus") ? tone : `Tone ${tone}`;
    }
};

export const TonePlayer: React.FC<{
    tones: string[];
    selectedTone: string;
    id: string;
    onSetModal: (id: string, element: JSX.Element) => void;
    onSetSelectedTone?: (tone: string) => void;
}> = (props) => {
    const { tones, selectedTone, id, onSetModal, onSetSelectedTone } = props;

    const isOrdinariatePage = window.location.href.includes("/se/");

    const isSmallScreen = useIsSmallScreen();

    const title = isOrdinariatePage
        ? `Tone ${LIBER_TONES[selectedTone].liber}`
        : getToneTitle(selectedTone, isSmallScreen);

    const fullTone = getTone(selectedTone);

    const { settings, setSetting } = useSettings();
    const transposeByKey = "1";
    const transposeBy =
        settings.transpositions["1"] + getTransposeByFromMode("1");
    const increaseTransposeBy = useCallback(() => {
        setSetting("transpositions", {
            ...settings.transpositions,
            ["1"]: transposeBy + 1,
        });
    }, [settings, transposeBy]);
    const decreaseTransposeBy = useCallback(() => {
        setSetting("transpositions", {
            ...settings.transpositions,
            ["1"]: transposeBy - 1,
        });
    }, [settings, transposeBy]);

    const [highlightCount, setHighlightCount] = useState(0);

    const { popoverId, setPopoverId } = usePopover();

    useEffect(() => {
        const medScore = getScoreFromTone(
            fullTone,
            "offset",
            "include",
            "skip"
        );
        const endScore = getScoreFromTone(
            fullTone,
            "offset",
            "offset",
            "include"
        );
        const fullScore = getScoreFromTone(
            fullTone,
            "include",
            "include",
            "include"
        );

        const modal = (
            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 100000,
                }}
                className="modal"
            >
                <div
                    style={{
                        flexGrow: 1,
                        maxWidth: WIDTH,
                        boxSizing: "border-box",
                        display: popoverId === `tone-${id}` ? "block" : "none",
                        padding: isSmallScreen ? 4 : 16,
                        paddingBottom: isSmallScreen ? 0 : 16,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            border: `1px solid ${TEXT_COLOR}`,
                            borderRadius: 8,
                            borderBottomLeftRadius: isSmallScreen ? 0 : 8,
                            borderBottomRightRadius: isSmallScreen ? 0 : 8,
                            borderBottom: isSmallScreen
                                ? "none"
                                : `1px solid ${TEXT_COLOR}`,
                            boxShadow: `0 0 12px rgba(0, 0, 0, 0.4)`,
                            flexGrow: 1,
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: 4,
                                paddingLeft: 12,
                                paddingRight: 4,
                                borderBottom: `1px solid ${HORIZONTAL_RULE_COLOR}`,
                                backgroundColor: DARKENED_BACKGROUND_COLOR,
                            }}
                        >
                            <div style={{ fontWeight: "bold" }}>{title}</div>
                            <div style={{ flexGrow: 1 }}></div>
                            {tones.length > 1 ? (
                                tones
                                    .filter((tone) => tone !== selectedTone)
                                    .map((tone) => {
                                        return (
                                            <div style={{}}>
                                                Alternatively:{" "}
                                                <a
                                                    style={{
                                                        fontWeight: "bold",
                                                        cursor: "pointer",
                                                        textDecoration:
                                                            "underline",
                                                    }}
                                                    onClick={() => {
                                                        if (onSetSelectedTone) {
                                                            onSetSelectedTone(
                                                                tone
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {getToneTitle(
                                                        tone,
                                                        isSmallScreen
                                                    )}
                                                </a>
                                            </div>
                                        );
                                    })
                            ) : (
                                <></>
                            )}
                            <div style={{ flexGrow: 1 }}></div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    id="show-pitch"
                                    name="show-pitch"
                                    checked={settings.tuning === "enabled"}
                                    style={{ paddingTop: 2 }}
                                    onChange={() => {
                                        console.log("hi");
                                        if (settings.tuning === "enabled") {
                                            setSetting("tuning", "disabled");
                                        } else {
                                            setSetting("tuning", "enabled");
                                        }
                                    }}
                                />
                                <label
                                    htmlFor="show-pitch"
                                    style={{ paddingLeft: 4 }}
                                >
                                    Show pitch
                                </label>
                            </div>
                            <div style={{ flexGrow: 1 }}></div>
                            <div
                                className="new-button"
                                onClick={() => setPopoverId(undefined)}
                            >
                                Close
                            </div>
                        </div>
                        <div
                            style={{
                                paddingTop: 24,
                                paddingLeft: isSmallScreen ? 8 : 24,
                            }}
                        >
                            <ToneDiagram
                                toneId={selectedTone}
                                highlightCount={highlightCount}
                            />
                        </div>
                        <div
                            style={
                                isSmallScreen
                                    ? {
                                          borderTop: `1px solid ${HORIZONTAL_RULE_COLOR}`,
                                          display: "flex",
                                          flexDirection: "column-reverse",
                                          alignItems: "center",
                                          backgroundColor:
                                              DARKENED_BACKGROUND_COLOR,
                                      }
                                    : {
                                          borderTop: `1px solid ${HORIZONTAL_RULE_COLOR}`,
                                          display: "flex",
                                          flexDirection: "row",
                                          backgroundColor:
                                              DARKENED_BACKGROUND_COLOR,
                                      }
                            }
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: 8,
                                    paddingBottom: 4,
                                    flexGrow: 1,
                                }}
                            >
                                <div style={{ width: 80 }}>
                                    Pitch:{" "}
                                    {transposeBy >= 0
                                        ? `+${transposeBy}`
                                        : `${transposeBy}`}
                                </div>
                                <div
                                    className="new-button"
                                    onClick={decreaseTransposeBy}
                                >
                                    &darr; Lower
                                </div>
                                <div style={{ paddingLeft: 4 }}>
                                    <div
                                        className="new-button"
                                        onClick={increaseTransposeBy}
                                    >
                                        &uarr; Higher
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 4,
                                    paddingLeft: 12,
                                    paddingRight: 8,
                                }}
                            >
                                <PlayButton
                                    clef={fullTone.clef}
                                    score={medScore.score}
                                    title="Play Med"
                                    transposeBy={
                                        transposeBy +
                                        getTransposeByFromToneObject(
                                            LIBER_TONES[selectedTone]
                                        )
                                    }
                                    onSetHighlightCount={(count) =>
                                        setHighlightCount(
                                            count === 0
                                                ? 0
                                                : medScore.offset + count
                                        )
                                    }
                                />
                                <PlayButton
                                    clef={fullTone.clef}
                                    score={endScore.score}
                                    title="Play End"
                                    transposeBy={
                                        transposeBy +
                                        getTransposeByFromToneObject(
                                            LIBER_TONES[selectedTone]
                                        )
                                    }
                                    onSetHighlightCount={(count) =>
                                        setHighlightCount(
                                            count === 0
                                                ? 0
                                                : endScore.offset + count
                                        )
                                    }
                                />
                                <PlayButton
                                    clef={fullTone.clef}
                                    score={fullScore.score}
                                    title="Play Full"
                                    transposeBy={
                                        transposeBy +
                                        getTransposeByFromToneObject(
                                            LIBER_TONES[selectedTone]
                                        )
                                    }
                                    onSetHighlightCount={setHighlightCount}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 8,
                                paddingBottom: 20,
                                backgroundColor: DARKENED_BACKGROUND_COLOR,
                            }}
                        >
                            <div style={{ fontStyle: "italic" }}>
                                Don't forget to turn your sound on!
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

        onSetModal(`tone-${id}`, modal);
    }, [popoverId, highlightCount, transposeBy, selectedTone, settings.tuning]);

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <PlayButton
                    clef={fullTone.clef}
                    score={getStartFromTone(fullTone)}
                    title="Intone"
                    transposeBy={
                        transposeBy +
                        getTransposeByFromToneObject(LIBER_TONES[selectedTone])
                    }
                />
                <div style={{ paddingLeft: 4 }}>
                    <div
                        className="new-button"
                        onClick={() => setPopoverId(`tone-${id}`)}
                        style={{
                            opacity: popoverId === `tone-${id}` ? 0.5 : 1,
                        }}
                    >
                        <div>More...</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
